import { isInProgress } from "@helpers/exams";
import type { PracticeTestExam } from "@memorang/types";
import type { ItemTypeSession } from "@memorang/ui/components/ItemTypeDetailModal/types";
import { getSkillIcon } from "@memorang/ui/constants";
import type { IconType, LevelIconName } from "@memorang/ui/icon";
import type { TFunction } from "i18next";
import type { RecommendedSkillTask } from "../recommendations/adapters/dashboard-task-types";
import type { FrontendRecommendationOption } from "../recommendations/adapters/frontend-option-adapter";
import {
	GUIDANCE_KEY,
	type SkillTestRecommendation,
	formatDurationLabel,
} from "./recommended-tests";

export type SkillDetailEntry = {
	id: string;
	session: ItemTypeSession;
	insight: string;
	isResume: boolean;
} & (
	| { start: "test"; examId: string }
	| { start: "practice"; task: RecommendedSkillTask }
);

const testInsightKey = (
	kind: SkillTestRecommendation["kind"],
	mode: "unknown" | "known",
): string => {
	if (kind === "resume") {
		return "dashboard.recommendedTasks.activeTestBody";
	}
	if (mode === "unknown" && kind === "diagnostic") {
		return "skillDetail.diagnosticInsight";
	}
	return GUIDANCE_KEY[kind];
};

export const testToSkillDetailEntry = ({
	kind,
	exam,
	skillIcon,
	mode,
	t,
}: {
	kind: SkillTestRecommendation["kind"];
	exam: PracticeTestExam;
	skillIcon: IconType;
	mode: "unknown" | "known";
	t: TFunction;
}): SkillDetailEntry => ({
	id: exam.id,
	start: "test",
	examId: exam.id,
	isResume: isInProgress(exam),
	insight: t(testInsightKey(kind, mode)),
	session: {
		id: exam.id,
		icon: skillIcon,
		title: exam.title,
		itemsCount: exam.numItems,
		itemsLabel: t("sessionConfig.quickStudy.levelSessions.itemsLabelGeneric"),
		tags: [
			{ icon: "clock-outline", label: formatDurationLabel(exam.totalTime, t) },
		],
		...(exam.hasAccess === false ? { showUpgrade: true } : {}),
	},
});

export const practiceOptionToSkillDetailEntry = (
	option: FrontendRecommendationOption,
): SkillDetailEntry => ({
	id: option.id,
	start: "practice",
	task: option.task,
	isResume: false,
	insight: option.insight,
	session: {
		id: option.id,
		icon: option.quickStudySession.icon as LevelIconName,
		title: option.quickStudySession.title,
		itemsCount: option.itemTypeSession.itemsCount,
		itemsLabel: option.itemTypeSession.itemsLabel,
		tags: option.itemTypeSession.tags,
	},
});

export const legacyTaskToSkillDetailEntry = (
	task: RecommendedSkillTask,
	index: number,
	t: TFunction,
): SkillDetailEntry | undefined => {
	const { quickStudyMeta } = task;
	if (!quickStudyMeta) {
		return undefined;
	}
	const id = `legacy-${index}`;
	return {
		id,
		start: "practice",
		task,
		isResume: false,
		insight: task.body ?? "",
		session: {
			id,
			icon: getSkillIcon(task.recommendation.skill.title),
			title: task.title,
			itemsCount: quickStudyMeta.numItems,
			itemsLabel: t("sessionConfig.quickStudy.levelSessions.itemsLabelGeneric"),
			tags: [
				{
					icon: "clock-outline",
					label: t("sessionConfig.quickStudy.duration", {
						minutes: quickStudyMeta.minutes,
					}),
				},
			],
		},
	};
};
